<template>
  <component
    :is="tag"
    v-if="$slots.default"
    :class="['dn-infobox',{'-active': fullview}]"
  >
    <atlas-headline
      v-if="title"
      class="title"
      tag="h3"
      :text="title"
    />
    <div class="content">
      <slot />
    </div>
    <atlas-button
      v-if="buttonText && fullview"
      v-track-event="toggleTrack"
      icon="common/chevron-up"
      class="secondary fullwidth"
      @click="toggleView"
    >
      {{ buttonText }}
    </atlas-button>
    <atlas-button
      v-if="buttonText && !fullview"
      v-track-event="toggleTrack"
      icon="common/chevron-down"
      class="secondary fullwidth"
      @click="toggleView"
    >
      {{ buttonText }}
    </atlas-button>
    <div
      v-if="disclosureInfo.content"
      class="disclosure"
    >
      <dn-icon
        v-if="isDisclosure"
        v-track-event="disclosureInfoTrack"
        :name="disclosureInfo.offIcon"
        @click="toggleDisclosure"
      />
      <dn-icon
        v-else
        v-track-event="disclosureInfoTrack"
        :name="disclosureInfo.onIcon"
        @click="toggleDisclosure"
      />
    </div>
    <dn-text-entity
      v-if="isDisclosure"
      class="disclosure-content"
      :content="disclosureInfo.content"
    />
  </component>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { AtlasButton, AtlasHeadline } from '@nhst/ui';
const { t } = useI18n();
const fullview = ref(false);
const isDisclosure = ref(false);

const props = defineProps({
  tag: {
    type: String,
    required: false,
    default: 'article',
    validator: (value: string) => ['article', 'aside', 'div'].includes(value)
  },
  title: {
    type: String,
    required: false,
    default: null,
    validator: (value: string) => value.length > 0
  },
  disclosure: {
    type: Object,
    required: false,
    default: () => {
    }
  },
  toggle: {
    type: Object,
    required: false,
    default: () => {
    }
  }
});
const toggleButton = ref({
  off: t('Show less'),
  on: t('Show more'),
  active: false,
  track: { eventName: 'Infobox toggle', elementName: 'dn-button' },
  action: () => {
  },
  ...props.toggle
});

const disclosureInfo = ref({
  onIcon: 'info',
  offIcon: 'close',
  content: null,
  track: { eventName: 'Infobox disclosure', elementName: 'dn-icon' },
  action: () => {
  },
  ...props.disclosure
});

/*
 * New-shape bindings: eventName is preserved at the top level so the fan-out
 * directive can keep emitting the same legacy 'Click | <eventName>' string to
 * nhstDataLayer. component/attributes feed @nhst/tracking-lib directly.
 */
const disclosureInfoTrack = computed(() => ({
  eventName: `${isDisclosure.value ? 'Hide' : 'Show'} ${disclosureInfo.value.track.eventName}`,
  component: {
    zone: 'infobox',
    element: disclosureInfo.value.track?.elementName || 'dn-icon'
  }
}));

const toggleTrack = computed(() => ({
  eventName: `${fullview.value ? 'Hide' : 'Show'} ${toggleButton.value.track.eventName}`,
  component: {
    zone: 'infobox',
    element: toggleButton.value.track?.elementName || 'dn-button'
  }
}));

const buttonText = computed(() => fullview.value ? toggleButton.value.off : toggleButton.value.on);

if (toggleButton.value.active) {
  fullview.value = true;
}
const toggleView = () => {
  fullview.value = !fullview.value;
  toggleButton.value.action();
};

const toggleDisclosure = () => {
  disclosureInfo.value.track.status = fullview;
  isDisclosure.value = !isDisclosure.value;
  disclosureInfo.value.action();
};
</script>

<style lang="scss">
.dn-infobox {
  --_dn-infobox_grid-template: var(--dn-infobox_grid-template, auto/ auto 24px);
  --_dn-infobox_gap: var(--dn-infobox_gap,  var(--atlas_gap));
  --_dn-infobox_background-color: var(--dn-infobox_background-color, #f9f2ef);
  --_dn-infobox_border-radius: var(--dn-infobox_border-radius, var(--border-radius-default-min));
  --_dn-infobox_padding: var(--dn-infobox_padding, var(--atlas_gap));
  --_dn-infobox_title_grid-area: var(--dn-infobox_title_grid-area, 1 / 1 / 2 / 2);
  --_dn-infobox_disclosure_grid-area: var(--dn-infobox_disclosure_grid-area, 1 / 2 / 2 / 3);
  --_dn-infobox_disclosure-content_grid-area: var(--dn-infobox_disclosure-content_grid-area, 2 / 1 / 3 / 3);
  --_dn-infobox_content_grid-area: var(--dn-infobox_content_grid-area, auto / 1 / auto / 3);
  --_dn-infobox_content_max-block-size: var(--dn-infobox_content_max-block-size, 3lh);
  --_dn-infobox_button_grid-area: var(--dn-infobox_button_grid-area, auto / 1 / auto / 3);
  --_dn-infobox_button_icon_background-color: var(--dn-infobox_button_icon_background-color, #000);
  --_dn-infobox_button_icon_size: var(--dn-infobox_button_icon_size, 12px);
  --_dn-infobox_button_icon_clip-path: var(--dn-infobox_button_icon_clip, polygon(5% 34%, 11% 28%, 50% 64%, 87% 28%, 93% 34%, 50% 75%));
  --_dn-infobox_button_icon_clip-path__active: var(--dn-infobox_button_icon_clip-path__active, polygon(5% 68%, 11% 74%, 50% 43%, 87% 73%, 92% 67%, 50% 34%));

  background-color: var(--_dn-infobox_background-color);
  border-radius: var(--_dn-infobox_border-radius);
  container-type: inline-size;
  padding: var(--_dn-infobox_padding);
  position: relative;
  display: grid;
  gap: var(--_dn-infobox_gap);
  grid-template: var(--_dn-infobox_grid-template);

  > .title {
    --dn-headline_color: var(--dn-infobox_title_color);
    --dn-headline_font-family: var(--dn-infobox_title_font-family);
    --dn-headline_font-size: var(--dn-infobox_title_font-size);
    --dn-headline_font-weight: var(--dn-infobox_title_font-weight);
    --dn-headline_line-height: var(--dn-infobox_title_line-height);

    grid-area: var(--_dn-infobox_title_grid-area);
  }

  > .content {
    --dn_color: var(--dn-infobox_content_color);
    --dn_font-family: var(--dn-infobox_content_font-family);
    --dn_font-size: var(--dn-infobox_content_font-size);
    --dn_line-height: var(--dn-infobox_content_line-height);

    grid-area: var(--_dn-infobox_content_grid-area);
  }

  > .disclosure {
    grid-area: var(--_dn-infobox_disclosure_grid-area);

    .dn-icon {
      cursor: pointer;
      z-index: 2;
    }
  }

  > .disclosure-content {
    --dn_color: var(--dn-infobox_disclosure-content_color);
    --dn_font-family: var(--dn-infobox_disclosure-content_font-family);
    --dn_font-size: var(--dn-infobox_disclosure-content_font-size);
    --dn_line-height: var(--dn-infobox_disclosure-content_line-height);

    grid-area: var(--_dn-infobox_disclosure-content_grid-area);
  }

  > .atlas-button {
    grid-area: var(--_dn-infobox_button_grid-area);

    @media only screen and (width >= 768px) {
      max-width: fit-content;

    }
  }

  &.-active {
    > .atlas-button::before {
      --_dn-infobox_button_icon_clip-path: var(--_dn-infobox_button_icon_clip-path__active);
    }
  }

  &:not(.-active):has(> .atlas-button) {
    > .content {
      max-block-size: var(--_dn-infobox_content_max-block-size);
      overflow: clip;
      position: relative;

      &::after {
        content: '';
        position: absolute;
        background-color: var(--_dn-infobox_background-color);
        bottom: 0;
        left: 0;
        width: 100%;
        height: 75%;
        mask-image: linear-gradient(to top, #000, transparent);
      }
    }

  }

}
</style>
